import * as turf from '@turf/turf';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import { lv95ToWgs84ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';

export type ShadowHourAndAngle = {
  hour: string;
  azimut: number;
  elevation: number;
};

type ShadowParameters = {
  maximalShadowDuration: string;
  referenceDays: string;
  shadowSegments: number;
  hoursAndAngles: ShadowHourAndAngle[];
};

const PARAMETERS_BY_RULE: Record<'ZH' | 'BS', ShadowParameters> = {
  ZH: {
    maximalShadowDuration: '3h',
    referenceDays: 'Average Winter Days (03.11 and 08.02)',
    shadowSegments: 10,
    hoursAndAngles: [
      { hour: '08:00', azimut: 62.6, elevation: 4.2 },
      { hour: '09:00', azimut: 50.6, elevation: 12.4 },
      { hour: '10:00', azimut: 37.3, elevation: 19.6 },
      { hour: '11:00', azimut: 22.7, elevation: 24.4 },
      { hour: '12:00', azimut: 6.8, elevation: 26.9 },
      { hour: '13:00', azimut: -9.3, elevation: 26.8 },
      { hour: '14:00', azimut: -25.1, elevation: 23.8 },
      { hour: '15:00', azimut: -39.6, elevation: 18.3 },
      { hour: '16:00', azimut: -52.4, elevation: 11.2 },
      { hour: '17:00', azimut: -64.2, elevation: 2.7 },
    ],
  },
  BS: {
    maximalShadowDuration: '2h',
    referenceDays: 'Equinoxes (21.03 and 23.09)',
    shadowSegments: 11,
    hoursAndAngles: [
      { hour: '07:30', azimut: -79, elevation: 10.13 },
      { hour: '08:30', azimut: -67, elevation: 19.76 },
      { hour: '09:30', azimut: -53.75, elevation: 28.51 },
      { hour: '10:30', azimut: -38, elevation: 35.76 },
      { hour: '11:30', azimut: -20, elevation: 40.66 },
      { hour: '12:30', azimut: 0, elevation: 42.43 },
      { hour: '13:30', azimut: 20, elevation: 40.66 },
      { hour: '14:30', azimut: 38, elevation: 35.76 },
      { hour: '15:30', azimut: 53.75, elevation: 28.51 },
      { hour: '16:30', azimut: 67, elevation: 19.76 },
      { hour: '17:30', azimut: 79, elevation: 10.13 },
    ],
  },
};

export const getShadowParameters = (rule: string): ShadowParameters => PARAMETERS_BY_RULE[rule === 'BS' ? 'BS' : 'ZH'];

export const calculateShadowCircle = (
  analysisArea: Array<{ x?: unknown; y?: unknown }>,
  height: number,
  rule: string
) => {
  // radius = boundingSquareDiagonal / 2 + gridHeight / tan(minElevation)
  const coordinates = analysisArea
    .map(({ x, y }) => ({ x: Number(x), y: Number(y) }))
    .filter(({ x, y }) => Number.isFinite(x) && Number.isFinite(y));
  if (coordinates.length < 3 || !Number.isFinite(height) || height < 0) return null;

  const bounds = coordinates.reduce(
    (current, coordinate) => ({
      minX: Math.min(current.minX, coordinate.x),
      maxX: Math.max(current.maxX, coordinate.x),
      minY: Math.min(current.minY, coordinate.y),
      maxY: Math.max(current.maxY, coordinate.y),
    }),
    {
      minX: coordinates[0].x,
      maxX: coordinates[0].x,
      minY: coordinates[0].y,
      maxY: coordinates[0].y,
    }
  );
  const diagonal = Math.sqrt(
    Math.pow((bounds.maxX - bounds.minX) / 2, 2) + Math.pow((bounds.maxY - bounds.minY) / 2, 2)
  );
  const minimumElevation = Math.min(...getShadowParameters(rule).hoursAndAngles.map(({ elevation }) => elevation));

  return {
    center: { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 },
    radius: diagonal + height / Math.tan((minimumElevation * Math.PI) / 180),
  };
};

type ShadowShape = Feature<Polygon | MultiPolygon>;

export const calculateShadowDiagram = (
  analysisArea: Array<{ x?: unknown; y?: unknown }>,
  height: number,
  rule: string
): { hourly: Array<{ hour: string; shape: ShadowShape }>; overAllowedHours: ShadowShape | null } => {
  const points = analysisArea
    .map(({ x, y }) => [Number(x), Number(y)])
    .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  if (
    points.length > 3 &&
    points[0][0] === points[points.length - 1][0] &&
    points[0][1] === points[points.length - 1][1]
  ) {
    points.pop();
  }
  if (points.length < 3 || !Number.isFinite(height) || height <= 0) return { hourly: [], overAllowedHours: null };

  const ring = [...points, points[0]];
  const footprint = turf.polygon([ring]);
  const hourly: Array<{ hour: string; shape: ShadowShape }> = [];
  const parameters = getShadowParameters(rule);
  const allowedHours = parseInt(parameters.maximalShadowDuration, 10);
  const atLeast: Array<ShadowShape | null> = Array(allowedHours + 1).fill(null);

  for (const { hour, azimut, elevation } of parameters.hoursAndAngles) {
    const distance = height / Math.tan((elevation * Math.PI) / 180);
    const bearing = (azimut * (rule === 'BS' ? 1 : -1) * Math.PI) / 180;
    const dx = distance * Math.sin(bearing);
    const dy = distance * Math.cos(bearing);
    const moved = points.map(([x, y]) => [x + dx, y + dy]);
    const pieces = [footprint, turf.polygon([[...moved, moved[0]]])];
    for (let i = 0; i < points.length; i++) {
      const next = (i + 1) % points.length;
      pieces.push(turf.polygon([[points[i], points[next], moved[next], moved[i], points[i]]]));
    }

    try {
      const shape = turf.union(turf.featureCollection(pieces));
      if (!shape) continue;
      hourly.push({ hour, shape });
      for (let count = Math.min(hourly.length, atLeast.length) - 1; count > 0; count--) {
        const previous = atLeast[count - 1];
        if (!previous) continue;
        const overlap = turf.intersect(turf.featureCollection([previous, shape]));
        const current = atLeast[count];
        if (overlap) atLeast[count] = current ? turf.union(turf.featureCollection([current, overlap])) : overlap;
      }
      atLeast[0] = atLeast[0] ? turf.union(turf.featureCollection([atLeast[0], shape])) : shape;
    } catch {
      // An invalid footprint cannot produce a reliable projected polygon.
      return { hourly: [], overAllowedHours: null };
    }
  }

  return {
    hourly,
    overAllowedHours: atLeast[allowedHours] ? turf.convex(atLeast[allowedHours]) : null,
  };
};

export const doesAreaIntersectShadowCircle = (
  area: Array<{ x?: unknown; y?: unknown }>,
  circle: NonNullable<ReturnType<typeof calculateShadowCircle>>
) => {
  const coordinates = area
    .map(({ x, y }) => ({ x: Number(x), y: Number(y) }))
    .filter(({ x, y }) => Number.isFinite(x) && Number.isFinite(y))
    .map(({ x, y }) => lv95ToWgs84ForOneCoordinate(x, y));
  if (coordinates.length < 3) return false;

  const ring = coordinates.map(({ lng, lat }) => [lng, lat]);
  ring.push([...ring[0]]);
  const center = lv95ToWgs84ForOneCoordinate(circle.center.x, circle.center.y);

  try {
    return turf.booleanIntersects(
      turf.polygon([ring]),
      turf.circle([center.lng, center.lat], circle.radius / 1000, { steps: 64, units: 'kilometers' })
    );
  } catch {
    return false;
  }
};
